import { Box, Button, Container, Typography } from '@mui/material';
import { useState } from 'react';

import { GridColDef } from '@mui/x-data-grid';
import Papa from 'papaparse';
import { getMaexchenExportData } from '../firebaseConnection/database';
import {
  MaexchenCollectCredit,
  MaexchenRedeemCredit,
} from '../types/transport';

type LogItem =
  | (MaexchenCollectCredit & { id: string })
  | (MaexchenRedeemCredit & { id: string });

const isRedeemedCredit = (
  composite: LogItem,
): composite is MaexchenRedeemCredit & { id: string } =>
  !!(composite as MaexchenRedeemCredit).totalCredits;

const convertTimestampToDate = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const withIds = <T,>(entries: Record<string, T> = {}) =>
  Object.entries(entries).map(([id, entry]) => ({ ...entry, id }));

const MaexchenUsersOverview = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();

  // loads everything once on click instead of keeping a live listener per
  // user group open, which made the page slow and ran out of memory
  const buildRows = async () => {
    const { users, byUserGroup, redeemProducts } =
      await getMaexchenExportData();

    const productNames = new Map(
      Object.values(redeemProducts).map(product => [
        product.ProduktId,
        product.Produktname,
      ]),
    );

    const getOrderItem = (element: MaexchenRedeemCredit) =>
      productNames.get(element.productId) +
      (element.productVariantName ? ` (${element.productVariantName})` : '');

    return Object.entries(users).flatMap(([userId, userData]) => {
      const userItem = { ...userData.profile, userId };
      const group = byUserGroup[userId] ?? {};
      const allEntries: LogItem[] = [
        ...withIds(group.collectedCredits),
        ...withIds(group.redeemedCredits),
      ].sort((a, b) => b.timestamp - a.timestamp);

      return allEntries.map(logItem => {
        let obj = {};
        if (isRedeemedCredit(logItem)) {
          obj = {
            id: logItem.id,
            timestamp: convertTimestampToDate(logItem.timestamp),
            log: `${logItem.totalCredits}`,
            productDescription: getOrderItem(logItem),
            type: 'redeemed',
          };
        } else {
          obj = {
            id: logItem.id,
            timestamp: convertTimestampToDate(logItem.timestamp),
            log: `${logItem.credits}`,
            productDescription: logItem.productDescription,
            type: 'collected',
            collectCode: logItem.collectCode,
          };
        }
        return {
          ...obj,
          user: userItem.userId,
          firstName: userItem.firstName,
          lastName: userItem.lastName,
          city: userItem.addressCity,
          addition: userItem.addressAddition,
          addressnumber: userItem.addressNumber,
          street: userItem.addressStreet,
          zip: userItem.addressZip,
          phone: userItem.phone,
          companyName: userItem.companyName,
          companyUid: userItem.companyUid,
          email: userItem.email,
        };
      });
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'timestamp',
      headerName: 'Datum',
      width: 100,
      editable: false,
    },
    {
      field: 'log',
      headerName: 'Buchung',
      width: 250,
      editable: false,
      renderCell: cellValues => (
        <Box sx={{ display: 'flex', justifyItems: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2">{cellValues.row.log}</Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'collectCode',
      headerName: 'Code',
      width: 250,
      editable: false,
    },
    {
      field: 'type',
      headerName: 'Typ',
      width: 250,
      editable: false,
    },
    {
      field: 'productDescription',
      headerName: 'Produktbeschreibung',
      width: 250,
      editable: false,
    },
    {
      field: 'firstName',
      headerName: 'Vorname',
      width: 120,
      editable: false,
    },
    {
      field: 'lastName',
      headerName: 'Nachname',
      width: 120,
      editable: false,
    },
    {
      field: 'email',
      headerName: 'E-Mail',
      width: 200,
      editable: false,
    },
    {
      field: 'phone',
      headerName: 'Telefon',
      width: 150,
      editable: false,
    },
    {
      field: 'street',
      headerName: 'Straße',
      width: 150,
      editable: false,
    },
    {
      field: 'addressnumber',
      headerName: 'Nr.',
      width: 70,
      editable: false,
    },
    {
      field: 'zip',
      headerName: 'PLZ',
      width: 80,
      editable: false,
    },
    {
      field: 'city',
      headerName: 'Ort',
      width: 120,
      editable: false,
    },
    {
      field: 'companyName',
      headerName: 'Firma',
      width: 150,
      editable: false,
    },
    {
      field: 'companyUid',
      headerName: 'UID',
      width: 150,
      editable: false,
    },
    {
      field: 'user',
      headerName: 'User-ID',
      width: 200,
      editable: false,
    },
  ];

  const downloadCsv = async () => {
    setIsLoading(true);
    setError(undefined);
    try {
      const rows = await buildRows();
      const csv = Papa.unparse({
        fields: columns.map(column => column.headerName ?? column.field),
        data: rows.map(row =>
          columns.map(
            column => (row as Record<string, unknown>)[column.field] ?? '',
          ),
        ),
      });
      const blob = new Blob(['﻿', csv], {
        type: 'text/csv;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'export.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : `${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container sx={{ width: `calc(100% - ${240}px)`, ml: `${240}px` }}>
      <Box
        component="main"
        sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}
      >
        <Typography variant="h4">Export Table</Typography>
      </Box>
      <Box style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Box>
          <Button
            variant="contained"
            onClick={downloadCsv}
            disabled={isLoading}
          >
            {isLoading ? 'Lade Daten…' : 'CSV herunterladen'}
          </Button>
        </Box>
        {error && <Typography color="error">{error}</Typography>}
      </Box>
    </Container>
  );
};

export default MaexchenUsersOverview;
